import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shadcn-ui/accordion';
import { AccordionListItem, ContentItem } from '@/common/types/accordion-list-item';
import { cn } from '@/shadcn-ui/utils';
import { Link } from 'react-router';
import React from 'react';
import Spacer from './spacer';
import Rule from './rule';
import { AccordionMultipleProps, AccordionSingleProps } from '@radix-ui/react-accordion';

interface AccordionListProps {
  accordionItems: AccordionListItem[];
  variant: 'dark' | 'default' | 'transparent';
  separateItemsWithHorizontalRule?: boolean;
  className?: string;
  defaultOpenItem?: number;
  type?: 'single' | 'multiple';
}

export default function AccordionList({
  accordionItems,
  variant,
  separateItemsWithHorizontalRule = false,
  className,
  defaultOpenItem,
  type = 'single',
}: AccordionListProps) {
  const isDark = variant === 'dark';
  const isTransparent = variant === 'transparent';

  const accordianProps: Record<string, AccordionSingleProps | AccordionMultipleProps> = {
    single: {
      type: 'single',
      collapsible: true,
      className: cn('w-full font-sans tracking-[-0.005em]', className),
      defaultValue: defaultOpenItem !== undefined ? `item-${defaultOpenItem}` : undefined,
    },
    multiple: {
      type: 'multiple',
      className: cn('w-full font-sans tracking-[-0.005em]', className),
    },
  };

  // Control the open state of the accordion items with this state. This assumes
  // that the accordion is single type, and the state only gets used when
  // icon overrides are provided for the trigger.
  const [openItem, setOpenItem] = React.useState<number | undefined>(defaultOpenItem);
  const handleClick = (index: number) =>
    setOpenItem((prev) => (prev === index ? undefined : index));

  return (
    <Accordion {...accordianProps[type]}>
      {accordionItems.map((item, index) => (
        <AccordionItem
          key={`item-${index}`}
          value={`item-${index}`}
          className={cn('w-full border-none px-[24px] text-flexcar-neutral100', {
            'mb-0 rounded-none px-0': separateItemsWithHorizontalRule,
            'mb-[24px] rounded-[16px]': !separateItemsWithHorizontalRule,
            'bg-flexcar-blue20': isDark,
            'bg-flexcar-neutral100': !isDark && !isTransparent,
          })}
        >
          <AccordionTrigger
            className={cn(
              'text-left font-sans hover:no-underline',
              {
                'pb-[16px] pt-0': separateItemsWithHorizontalRule,
                'pb-6': isTransparent,
                'text-flexcar-neutral10': !isDark,
                'text-flexcar-neutral100': isDark,
              },
              item.className
            )}
            onClick={() => handleClick(index)}
            open={openItem === index}
            iconOverride={item.iconOverride}
            iconVariant={isDark ? 'default' : 'dark'}
          >
            {item.title}
          </AccordionTrigger>

          <AccordionContent className="font-sans text-base">
            {/* Main content */}
            {item.content.map((contentItem: ContentItem, index) => (
              <React.Fragment key={index}>
                {contentItem.title ? (
                  <>
                    <p
                      className={cn('body-strong inline font-sans', {
                        'text-flexcar-neutral100': isDark,
                        'text-flexcar-neutral10': !isDark,
                      })}
                    >
                      {contentItem.title}
                    </p>
                    <p
                      className={cn('body inline font-sans', {
                        'text-flexcar-neutral100': isDark,
                        'text-flexcar-neutral10': !isDark,
                      })}
                    >
                      :{' '}
                    </p>
                  </>
                ) : null}
                <p
                  className={cn('body font-sans', {
                    'text-flexcar-neutral100': isDark,
                    'text-flexcar-neutral10': !isDark,
                  })}
                >
                  {contentItem.body}
                </p>
                {contentItem.link?.url && contentItem.link?.text ? (
                  <>
                    <p className="body inline font-sans text-flexcar-neutral100"> (</p>
                    <Link
                      to={contentItem.link.url}
                      reloadDocument={contentItem.link.reloadDocument}
                      className={cn('link-body inline font-medium', {
                        'text-flexcar-aqua50 hover:text-flexcar-aqua50 focus:text-flexcar-aqua50':
                          isDark,
                      })}
                    >
                      {contentItem.link.text}
                    </Link>
                    <p className="body inline font-sans text-flexcar-neutral100">)</p>
                  </>
                ) : null}

                {index !== item.content.length - 1 ? <Spacer variant="height8" /> : null}
              </React.Fragment>
            ))}

            {/* List after content */}
            {item.list ? (
              <ul className="mt-1">
                {item.list.map((listItem) => {
                  return (
                    <li
                      key={listItem}
                      className={cn('body', {
                        'text-flexcar-neutral100': isDark,
                        'text-flexcar-neutral10': !isDark,
                      })}
                    >
                      {listItem}
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {/* Link after content */}
            {item.link?.url ? (
              <React.Fragment>
                <Spacer variant="height16" />
                <Link
                  to={item.link.url}
                  reloadDocument={item?.link?.reloadDocument === true ? true : false} // Making TS happy here with this check
                  className={cn('link-body flex', {
                    'text-flexcar-neutral100 hover:text-flexcar-neutral100 focus:text-flexcar-neutral100':
                      isDark,
                  })}
                >{`${item.link.text}`}</Link>
              </React.Fragment>
            ) : null}

            {/* Footer */}
            {item.footer ? (
              <React.Fragment>
                <Spacer variant="height16" />
                {item.footer}
              </React.Fragment>
            ) : null}
          </AccordionContent>

          {index !== accordionItems.length - 1 && separateItemsWithHorizontalRule ? (
            <>
              <Rule
                direction="horizontal"
                color={isTransparent ? 'bg-flexcar-neutral80' : 'bg-flexcar-neutral90'}
                className={isTransparent ? 'h-[1px]' : 'h-[2px]'}
              />
              <Spacer variant={isTransparent ? 'height24' : 'height16'} />
            </>
          ) : null}
        </AccordionItem>
      ))}
    </Accordion>
  );
}
